import {DI_TOKENS} from '@/di/di.tokens';

import {injected} from 'brandi';

import {DateTimeService} from '@/services/DateTimeService/DateTimeService';

import {CacheStorageAbstract} from './CacheStorage/CacheStorageAbstract';

export enum CacheKey {
  Bonuses = 'bonuses',
  Onboarding = 'onboarding',
  OpenedAll = 'OpenedAll',
  ProjectFinishesSoon = 'ProjectFinishesSoon',
  ProjectFinished = 'ProjectFinished',
}

export class CacheService {
  constructor(
    private readonly cacheStorage: CacheStorageAbstract,
    private readonly dateTimeService: DateTimeService,
  ) {}

  public saveUnwatchedBonus(bonusId: string): void {
    const cacheCreatedAt = this.dateTimeService.todayDateToIsoString();
    const currentBonuses = this.getUnwatchedBonuses();

    this.cacheStorage.setCacheByKey(CacheKey.Bonuses, {
      data: [...currentBonuses, bonusId],
      cacheCreatedAt,
    });
  }

  public deleteWatchedBonus(bonusId: string): void {
    const cacheCreatedAt = this.dateTimeService.todayDateToIsoString();
    const currentBonuses = this.getUnwatchedBonuses();

    const updatedBonusesList = currentBonuses.filter((bonus) => bonus !== bonusId);

    if (updatedBonusesList.length === 0) {
      this.clearCacheByKey(CacheKey.Bonuses);
    } else {
      this.cacheStorage.setCacheByKey(CacheKey.Bonuses, {
        data: [...updatedBonusesList],
        cacheCreatedAt,
      });
    }
  }

  public getUnwatchedBonuses(): string[] {
    const cacheData = this.cacheStorage.getCacheByKey<string[]>(CacheKey.Bonuses);

    return cacheData !== null ? cacheData.data : [];
  }

  public saveOnboardingModalShown(): void {
    const cacheCreatedAt = this.dateTimeService.todayDateToIsoString();

    this.cacheStorage.setCacheByKey(CacheKey.Onboarding, {
      data: [cacheCreatedAt],
      cacheCreatedAt,
    });
  }

  public getIsOnboardingModalShown(): boolean {
    const cacheData = this.cacheStorage.getCacheByKey<string[]>(CacheKey.Onboarding);

    return cacheData !== null && cacheData.data.length !== 0;
  }

  public deleteIsOnboardingModalShown(): void {
    this.clearCacheByKey(CacheKey.Onboarding);
  }

  public clearCacheByKey = (key: CacheKey): void => {
    this.cacheStorage.clearCacheByKey(key);
  };

  public saveFinishesSoonModal(): void {
    const cacheCreatedAt = this.dateTimeService.todayDateToIsoString();

    this.cacheStorage.setCacheByKey(CacheKey.ProjectFinishesSoon, {
      data: [cacheCreatedAt],
      cacheCreatedAt,
    });
  }

  public getIsFinishesSoonModal(): boolean {
    const cacheData = this.cacheStorage.getCacheByKey<string[]>(CacheKey.ProjectFinishesSoon);

    return cacheData !== null && cacheData.data.length !== 0;
  }

  public saveFinishedModal(): void {
    const cacheCreatedAt = this.dateTimeService.todayDateToIsoString();

    this.cacheStorage.setCacheByKey(CacheKey.ProjectFinished, {
      data: [cacheCreatedAt],
      cacheCreatedAt,
    });
  }

  public getIsFinishedModal(): boolean {
    const cacheData = this.cacheStorage.getCacheByKey<string[]>(CacheKey.ProjectFinished);

    return cacheData !== null && cacheData.data.length !== 0;
  }

  public saveAllItemsOpenedModal(): void {
    const cacheCreatedAt = this.dateTimeService.todayDateToIsoString();

    this.cacheStorage.setCacheByKey(CacheKey.OpenedAll, {
      data: [cacheCreatedAt],
      cacheCreatedAt,
    });
  }

  public getIsAllItemsOpenedModal(): boolean {
    const cacheData = this.cacheStorage.getCacheByKey<string[]>(CacheKey.OpenedAll);

    return cacheData !== null && cacheData.data.length !== 0;
  }
}

injected(CacheService, DI_TOKENS.cacheStorage, DI_TOKENS.dateTimeService);
